/*
  Plus+Ultra card quick buy (Concept 01)
  One shared purchase selector for product cards: centered dialog on desktop, bottom sheet on phones.
  Data comes from snippets/pu-quickbuy.liquid (<script type="application/json" data-pu-qb-data>).
  Adds to cart the same way the theme's product-form does (cart/add.js + bundled sections, then
  variant:add and cart:change), so the cart drawer opens and refreshes.
  Loaded as a module from every card, which the browser executes only once per page.
*/
(() => {
  if (window.__puQuickBuy) return;
  window.__puQuickBuy = true;

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const DEFAULT_ERROR = 'We could not add this to your cart. Please try again, or see all options.';

  let dialog = null;
  let ui = null;
  let data = null;
  let opener = null;
  let isOpen = false;
  let busy = false;
  let downOnBackdrop = false;

  function loadCss() {
    if (document.querySelector('link[data-pu-qb-style]')) return;
    const src = document.querySelector('[data-pu-qb-css]');
    if (!src) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = src.getAttribute('data-pu-qb-css');
    link.setAttribute('data-pu-qb-style', '');
    document.head.appendChild(link);
  }

  function build() {
    dialog = document.createElement('dialog');
    dialog.className = 'pu-qb';
    dialog.id = 'pu-qb';
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-labelledby', 'pu-qb-title');
    dialog.innerHTML = `
      <div class="pu-qb__panel">
        <div class="pu-qb__head">
          <img class="pu-qb__img" alt="" width="56" height="56" data-qb-img>
          <div class="pu-qb__heading">
            <p class="pu-qb__eyebrow">Choose options</p>
            <h2 class="pu-qb__title" id="pu-qb-title" data-qb-title></h2>
          </div>
          <button type="button" class="pu-qb__close" data-qb-close aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" fill="none"/></svg>
          </button>
        </div>
        <form class="pu-qb__form" data-qb-form novalidate>
          <fieldset class="pu-qb__modes">
            <legend class="sr-only">Purchase option</legend>
            <label class="pu-qb__opt" data-qb-row="subscribe">
              <input type="radio" name="pu-qb-mode" value="subscribe" data-qb-input="subscribe">
              <span class="pu-qb__dot" aria-hidden="true"></span>
              <span class="pu-qb__opt-body">
                <span class="pu-qb__opt-top">
                  <span class="pu-qb__opt-title" data-qb-sub-title>Subscribe &amp; save</span>
                  <span class="pu-qb__opt-price" data-qb-sub-price></span>
                </span>
                <span class="pu-qb__opt-meta" data-qb-sub-meta></span>
                <span class="pu-qb__opt-note">Skip, pause or cancel anytime</span>
              </span>
            </label>
            <label class="pu-qb__opt" data-qb-row="one_time">
              <input type="radio" name="pu-qb-mode" value="one_time" data-qb-input="one_time">
              <span class="pu-qb__dot" aria-hidden="true"></span>
              <span class="pu-qb__opt-body">
                <span class="pu-qb__opt-top">
                  <span class="pu-qb__opt-title">One-time purchase</span>
                  <span class="pu-qb__opt-price" data-qb-ot-price></span>
                </span>
                <span class="pu-qb__opt-meta">Single delivery, no subscription</span>
              </span>
            </label>
          </fieldset>
          <p class="pu-qb__error" role="alert" data-qb-error hidden></p>
          <button type="submit" class="button button--lg w-full pu-qb__submit" data-qb-submit></button>
          <a class="pu-qb__all" href="#" data-qb-all>See all options</a>
        </form>
      </div>`;
    document.body.appendChild(dialog);

    const q = (sel) => dialog.querySelector(sel);
    ui = {
      img: q('[data-qb-img]'),
      title: q('[data-qb-title]'),
      form: q('[data-qb-form]'),
      subRow: q('[data-qb-row="subscribe"]'),
      subInput: q('[data-qb-input="subscribe"]'),
      subTitle: q('[data-qb-sub-title]'),
      subPrice: q('[data-qb-sub-price]'),
      subMeta: q('[data-qb-sub-meta]'),
      otRow: q('[data-qb-row="one_time"]'),
      otInput: q('[data-qb-input="one_time"]'),
      otPrice: q('[data-qb-ot-price]'),
      error: q('[data-qb-error]'),
      submit: q('[data-qb-submit]'),
      all: q('[data-qb-all]')
    };

    q('[data-qb-close]').addEventListener('click', close);
    // Backdrop: a click whose press and release both land outside the panel (the dialog box itself).
    dialog.addEventListener('pointerdown', (e) => { downOnBackdrop = e.target === dialog; });
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog && downOnBackdrop) close();
      downOnBackdrop = false;
    });
    // Esc closes natively; tidy up whenever the dialog closes, however it closed.
    dialog.addEventListener('close', cleanup);
    ui.form.addEventListener('change', (e) => { if (e.target.name === 'pu-qb-mode') render(); });
    ui.form.addEventListener('submit', submit);
  }

  function hasSub() { return !!(data && data.sub && data.sub.plan); }
  function hasOneTime() { return !!(data && data.oneTime && !data.requiresPlan); }
  function mode() { return hasSub() && ui.subInput.checked ? 'subscribe' : 'one_time'; }

  function render() {
    const m = mode();
    ui.subRow.classList.toggle('is-checked', m === 'subscribe');
    ui.otRow.classList.toggle('is-checked', m === 'one_time');
    const label = m === 'subscribe' ? `Subscribe · ${data.sub.money}` : `Add one-time · ${data.oneTime.money}`;
    ui.submit.textContent = label;
    dialog.dataset.mode = m;
    dialog.dataset.variant = String(data.variant);
    dialog.dataset.plan = m === 'subscribe' ? String(data.sub.plan) : '';
    dialog.dataset.total = String(m === 'subscribe' ? data.sub.price : data.oneTime.price);
  }

  function showError(message) {
    ui.error.textContent = message || DEFAULT_ERROR;
    ui.error.hidden = false;
  }

  function hideError() {
    ui.error.textContent = '';
    ui.error.hidden = true;
  }

  function open(trigger) {
    const wrap = trigger.closest('[data-pu-qb]');
    const node = wrap && wrap.querySelector('script[data-pu-qb-data]');
    if (!node) return;
    try {
      data = JSON.parse(node.textContent);
    } catch (err) {
      return;
    }
    if (!hasSub() && !hasOneTime()) {
      window.location.href = data.url;
      return;
    }
    if (!dialog) build();
    opener = trigger;

    if (data.image) {
      ui.img.src = data.image;
      ui.img.hidden = false;
    } else {
      ui.img.removeAttribute('src');
      ui.img.hidden = true;
    }
    ui.title.textContent = data.title;
    ui.all.href = data.url;

    const sub = hasSub();
    const ot = hasOneTime();
    ui.subRow.hidden = !sub;
    ui.subInput.disabled = !sub;
    ui.otRow.hidden = !ot;
    ui.otInput.disabled = !ot;
    if (sub) {
      ui.subTitle.textContent = data.sub.pct > 0 ? `Subscribe & save ${data.sub.pct}%` : 'Subscribe';
      ui.subPrice.textContent = data.sub.money;
      ui.subMeta.textContent = `${data.sub.perDelivery ? 'per delivery' : 'per billing'} · ${data.sub.cadence}`;
    }
    if (ot) ui.otPrice.textContent = data.oneTime.money;
    ui.subInput.checked = sub;
    ui.otInput.checked = !sub;

    hideError();
    render();
    document.documentElement.classList.add('pu-qb-lock');
    dialog.showModal();
    isOpen = true;
    (sub ? ui.subInput : ui.otInput).focus();
    document.dispatchEvent(new CustomEvent('pu:quickbuy:open', { detail: { handle: data.handle, variant: data.variant } }));
  }

  function cleanup() {
    if (!isOpen) return;
    isOpen = false;
    document.documentElement.classList.remove('pu-qb-lock');
    if (opener && opener.isConnected) opener.focus({ preventScroll: true });
  }

  function close() {
    if (dialog && dialog.open) dialog.close();
    cleanup();
  }

  async function submit(event) {
    event.preventDefault();
    if (busy || !data) return;

    const m = mode();
    const variantId = data.variant;
    const planId = m === 'subscribe' ? data.sub.plan : null;
    const price = m === 'subscribe' ? data.sub.price : data.oneTime.price;

    busy = true;
    hideError();
    ui.submit.setAttribute('aria-busy', 'true');

    const sections = ['variant-added'];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));

    const body = new FormData();
    body.set('id', String(variantId));
    body.set('quantity', '1');
    if (planId) body.set('selling_plan', String(planId));
    body.set('sections', sections.join(','));
    body.set('sections_url', `${ROOT}variants/${variantId}`);

    try {
      const response = await fetch(`${ROOT}cart/add.js`, {
        method: 'POST',
        body,
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      let json = {};
      try { json = await response.json(); } catch (err) { json = {}; }

      if (!response.ok) {
        showError(typeof json.description === 'string' && json.description ? json.description : (json.message || DEFAULT_ERROR));
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        return;
      }

      document.dispatchEvent(new CustomEvent('pu:quickbuy:add', { detail: { handle: data.handle, variant: variantId, sellingPlan: planId, mode: m, price } }));

      const settings = (window.themeVariables && window.themeVariables.settings) || {};
      const hasDrawer = settings.cartType === 'drawer' ? document.querySelector('cart-drawer') : settings.cartType === 'popover' ? document.querySelector('cart-notification-drawer') : null;
      if (settings.pageType === 'cart' || !hasDrawer) {
        window.location.href = `${ROOT}cart`;
        return;
      }

      let cart;
      try {
        cart = await (await fetch(`${ROOT}cart.js`)).json();
      } catch (err) {
        window.location.href = `${ROOT}cart`;
        return;
      }
      cart.sections = json.sections;

      const source = opener && opener.isConnected ? opener : document.documentElement;
      close();
      source.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: json.hasOwnProperty('items') ? json.items : [json], cart } }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));
    } catch (err) {
      showError(DEFAULT_ERROR);
    } finally {
      busy = false;
      if (ui) ui.submit.removeAttribute('aria-busy');
    }
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest && event.target.closest('[data-pu-qb-open]');
    if (!trigger) return;
    event.preventDefault();
    open(trigger);
  });

  loadCss();
})();
